import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Eye, Inbox, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AdminHeader, EmptyState } from "./AdminLayout";

const STATUSES = ["New", "Contacted", "Won", "Lost"];
const STATUS_CLS = {
  New: "text-sky-300 border-sky-400/30 bg-sky-400/10",
  Contacted: "text-yellow-300 border-yellow-400/30 bg-yellow-400/10",
  Won: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
  Lost: "text-red-300 border-red-400/30 bg-red-400/10",
};
const fmt = (iso) => format(new Date(iso), "MMM d, yyyy · h:mm a");

const StatusSelect = ({ lead, onChange }) => (
  <select value={lead.status} onChange={(e) => onChange(lead, e.target.value)} data-testid={`lead-status-select-${lead.id}`}
    className={`rounded-full border px-3 py-1 text-xs font-medium outline-none ${STATUS_CLS[lead.status]}`}>
    {STATUSES.map((s) => <option key={s} value={s} className="bg-[#0D111A] text-white">{s}</option>)}
  </select>
);

const LeadDialog = ({ lead, onClose }) => (
  <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid="lead-detail-dialog" className="max-w-lg border-white/10 bg-[#0D111A]">
      {lead && (
        <>
          <DialogHeader><DialogTitle className="text-white">{lead.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <a href={`mailto:${lead.email}`} className="block break-all text-cyan-300">{lead.email}</a>
            <p className="text-slate-500">{fmt(lead.created_at)} · {lead.source || lead.project_type || "No project type"}</p>
            <p data-testid="lead-detail-message" className="whitespace-pre-wrap rounded-lg border border-white/5 bg-black/20 p-4 text-slate-200">{lead.message}</p>
            <p className="font-mono-tech text-xs text-slate-500">owner email: {lead.owner_email_status} · auto-reply: {lead.autoreply_status}</p>
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);

export default function Leads() {
  const [leads, setLeads] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/admin/leads").then((r) => setLeads(r.data)).catch((e) => { setLeads([]); toast.error(formatApiErrorDetail(e.response?.data?.detail)); });
  }, []);

  const changeStatus = async (lead, status) => {
    try {
      const { data } = await api.patch(`/admin/leads/${lead.id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === data.id ? data : l)));
      toast.success(`Marked ${data.name} as ${status}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const shown = useMemo(() => (leads || []).filter((l) =>
    (filter === "All" || l.status === filter) &&
    `${l.name} ${l.email}`.toLowerCase().includes(q.toLowerCase())), [leads, q, filter]);

  return (
    <div data-testid="admin-leads-page">
      <AdminHeader eyebrow="// inbox" title="Leads">{leads ? `${leads.length} total · ${leads.filter((l) => l.status === "New").length} new` : "Loading..."}</AdminHeader>
      {leads === null ? <div className="h-40 animate-pulse rounded-2xl bg-white/[0.03]" /> : leads.length === 0 ? (
        <EmptyState icon={Inbox} title="No inquiries yet" text="New contact form submissions will appear here." testId="admin-leads-empty-state" />
      ) : (
        <>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" data-testid="leads-search-input"
                className="w-full rounded-lg border border-white/10 bg-[#0B0F18] py-2.5 pl-9 pr-3 text-sm text-white outline-none focus:border-cyan-400/60" />
            </div>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="leads-status-filter"
              className="rounded-lg border border-white/10 bg-[#0B0F18] px-3 py-2.5 text-sm text-white outline-none">
              {["All", ...STATUSES].map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-white/5 bg-[#0B0F18]">
            <table data-testid="admin-leads-table" className="w-full min-w-[640px] text-left text-sm">
              <thead className="border-b border-white/5 font-mono-tech text-[11px] uppercase tracking-wider text-slate-500">
                <tr><th className="px-5 py-3">Name</th><th className="px-5 py-3">Email</th><th className="px-5 py-3">Date</th><th className="px-5 py-3">Status</th><th className="px-5 py-3" /></tr>
              </thead>
              <tbody>
                {shown.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-white/5 transition-colors last:border-0 hover:bg-white/[0.02]">
                    <td className="px-5 py-4 font-medium text-white">{l.name}{l.source && <span data-testid={`lead-source-${l.id}`} className="mt-1 block w-fit rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2 py-0.5 text-[11px] font-normal text-cyan-300">{l.source}</span>}</td>
                    <td className="px-5 py-4 text-slate-400">{l.email}</td>
                    <td className="whitespace-nowrap px-5 py-4 text-slate-400">{fmt(l.created_at)}</td>
                    <td className="px-5 py-4"><StatusSelect lead={l} onChange={changeStatus} /></td>
                    <td className="px-5 py-4 text-right">
                      <button onClick={() => setOpen(l)} data-testid={`lead-view-button-${l.id}`} className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-300"><Eye className="h-3.5 w-3.5" /> View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <LeadDialog lead={open} onClose={() => setOpen(null)} />
    </div>
  );
}
